/**
 * The game's money and time (M238, the overhaul's brief, section 3: "values configurable and separate from the
 * science"): every figure the conservancy's budget, staff, boat, surveys and rule-making run on, here alone, so a
 * change to play touches no rate of the ocean's. None of them moves the ecosystem: money buys effort (a survey's
 * sites, an officer's hours, a rule's process), and the effort acts on the simulation only through the inputs it
 * already had (the monitoring's error, `management.ts`'s patrols and rules).
 *
 * Each value is the game's (the user's word, M178: "realistic enough ... but not so convoluted that the game isnt
 * fun"), set by hand for play, and says what it was set against.
 */

/** A staff member's role: who dives, who runs the boat, who works with the communities. */
export type Role = 'biologist' | 'captain' | 'liaison';

export const ROLE_NAMES: Readonly<Record<Role, string>> = { biologist: 'Diver (field biologist)', captain: 'Boat captain', liaison: 'Community liaison' };

/**
 * The kinds of trip the conservancy may send its boat on, each a habitat's and its programs' (`SURVEY_KINDS`); and from
 * M307 the shore cleanup, the boat's first field mission, a trip that counts nothing (`shoreDebris.ts`).
 */
export type SurveyKind = 'reefFish' | 'reefBenthic' | 'reefBleaching' | 'slopeCamera' | 'shoreCleanup';

export const ECONOMY = {
  /**
   * Funds at the opening, US dollars: the half year to the State's fiscal year (1 July, below) at the year's operating
   * grant, so the first half year is run on what the first grant left (JUDGED).
   */
  openingFunds: 360_000,
  /*
   * The year's grant is set against NOAA's coral reef cooperative agreement with Hawaiʻi's Division of Aquatic
   * Resources, "Budget: $1,429,063.00" over October 2021 to September 2023, "Year 1 Total $574,958" and "Year 2 Total
   * $854,105" (DAR, FY2021-2022 Hawaiʻi Coral Reef Management Grant, Final Report NA21NOS4820017, 2024, pp. 1 and 4, a
   * US government award's report): about $715,000 a year, the State's whole coral reef program's federal share, here
   * one sanctuary's whole budget (JUDGED, a game's: enough for its crew, its boat and a few months' surveys a year).
   */
  /** The year's operating grant, US dollars, paid on the fiscal year's first day, Hawaiʻi's State's 1 July. */
  operatingGrantPerYear: 720_000,
  grantDay: { month: 7, day: 1 },
  /**
   * Each role's pay a month, US dollars, with its benefits: set against the State's own, DOCARE's officers' "Starting
   * pay (depending on position) ranges from $3847.00 – $6935.00 per month" (DLNR DOCARE, news release, 25 April 2017),
   * near its top with the benefits a State employer adds (JUDGED; the State's salary schedules for its aquatic
   * biologists were refused this environment: open).
   */
  monthlyPay: { biologist: 6_500, captain: 6_000, liaison: 5_500 } as Readonly<Record<Role, number>>,
  /** The opening crew: two biologists, so a dive has its buddy; a captain; a liaison. */
  openingCrew: [
    { name: 'Kalena', role: 'biologist' },
    { name: 'Makoa', role: 'biologist' },
    { name: 'Keoni', role: 'captain' },
    { name: 'Noelani', role: 'liaison' },
  ] as readonly { name: string; role: Role }[],
  /** Names for those hired after the opening, in turn. */
  hireNames: ['Ikaika', 'Leilani', 'Kaimana', 'Malia', 'Kawika', 'Pualani', 'Nāinoa', 'Healani'] as readonly string[],
  /** The most of a role the conservancy may employ (its boat holds two dive teams and its captain; JUDGED). */
  mostOf: { biologist: 4, captain: 1, liaison: 2 } as Readonly<Record<Role, number>>,
  /**
   * The boat's upkeep a month (mooring, insurance, maintenance) and its fuel and running a day at sea, US dollars: the
   * day set against Hawaiʻi's small boats' own, "The average trip cost for all trips is $302", fuel, oil, ice, bait,
   * food, maintenance and gear lost, on vessels averaging "24 feet" (NOAA PIFSC, Hawaiʻi Small Boat Survey 2021 Summary,
   * 19 April 2022, a US government work), a survey boat's longer day and its divers' gear taking more (JUDGED; no
   * research boat's day rate could be read: open). The month's upkeep JUDGED too, set against nothing read: about a
   * week's fuel, its mooring, insurance and haul-outs (codebase review 29: its only figure is a trip's).
   */
  boat: { name: 'Kilo Kai', upkeepPerMonth: 3_000, fuelPerDay: 450 },
  /**
   * A survey's field costs a day at sea besides the fuel (tanks, gear, the camera's and the divers' consumables), US
   * dollars. With the crew's pay by the calendar day (a pair's and the captain's, $625) and the fuel, a reef survey's day
   * comes to $1,375 for its two sites, $687 a site ($1,473 with the boat's upkeep; codebase review 29 corrected the
   * "$1,700" here, which spread the pay over working days), set against
   * DAR's West Hawaiʻi monitoring, $220,000 and $230,000 over two years for "395 dives ... and an additional 28 dives"
   * (the report above, pp. 4, 26 and 27), $1,064 a dive, the grant's share alone (JUDGED).
   */
  // The cameras' day twice a dive's: their frames, ballast and the lander's handling (JUDGED, set against the dive's
  // alone; no camera survey's cost could be read, BFISH's access asked of PIFSC, M184).
  // A shore cleanup's day the reef surveys' (M307's design, `docs/FIELD_LOOP_ROADMAP.md`): disposal, bags and supplies in
  // place of the divers' gear (JUDGED, set against the reef survey's day; no cleanup's costs a day were read).
  fieldCostPerDay: { reefFish: 300, reefBenthic: 300, reefBleaching: 300, slopeCamera: 600, shoreCleanup: 300 } as Readonly<Record<SurveyKind, number>>,
  /** The days a survey may run: a week, two, four of field days (JUDGED). */
  surveyDays: [5, 10, 20] as readonly number[],
  /**
   * A dive pair's sites a day on the reef: NOAA's own at Maui Nui in 2024, 94 sites on 95 diver-days over 12 days, 1.98
   * a pair (MEASURED from the archived replicates, `data/ncrmp-fish-maui-nui`, every 2024 row: the sites a day over half
   * its divers), "A pair of divers surveyed the fish assemblage at each site" (PIFSC, Fish Monitoring Brief: Hawaiʻi,
   * 2024, p. 2).
   */
  reefSitesPerDay: 2,
  /** A data report's research grant on each finished survey, US dollars (JUDGED: about a week's field costs). */
  researchGrantPerSurvey: 5_000,
  /**
   * The tourism partners' fees a month at the reef's 2024 cover, scaled by its cover now (JUDGED, set against the
   * opening's running costs: about a third of a month's, so a reef's health shows in the accounts without carrying them;
   * no dive operators' fees to a reserve could be read: open).
   */
  tourismPerMonth: 8_000,
  /**
   * A DOCARE officer's hour the conservancy pays for beside the State's, US dollars: DOCARE's top starting pay above,
   * $6,935 a month, over a month's 173 working hours, $40 an hour, with the State's benefits and the patrol's vehicle and
   * boat (JUDGED).
   */
  officerHour: 55,
  /**
   * A rule's process (the brief's section 5: proposed by the sanctuary, set by the State): the conservancy's
   * consultation with the island's communities, its liaison's months, then the State's rule-making; and the
   * consultation's cost (meetings, outreach, travel), US dollars. The State's days Maui's Kahekili Herbivore Fisheries
   * Management Area took, from its public notice "in the Honolulu Star-Bulletin and the Maui News on December 28, 2008"
   * to its effect "ten days after filing with the Office of the Lieutenant Governor", filed "JUL 15 2009" (HAR chapter
   * 13-60.7, the certified copy, p. 60.7-5): 209 days (MEASURED, the calendar's); its consultation before the notice was not found (open), so four months (JUDGED: the
   * State's own herbivore rules of 2020 to 2024 took 1,210 days from their scoping to their effect, longer than play
   * stays fun). Its cost JUDGED.
   */
  rule: { consultationDays: 120, stateDays: 209, consultationCost: 15_000 },
  /**
   * The terms a patrol may be bought for, days (M274, the redesign's section 5: "shorter patrol commitments, prorated
   * cost and effectiveness, no year paid upfront"): a month, a season, or until stopped (JUDGED: DOCARE's units are
   * funded by the year, its overtime by the shift; a month the shortest a schedule of officers is worth drawing up).
   */
  patrolTerms: [30, 90] as readonly number[],
  /**
   * A bleaching assessment's days at sea, at most (M274): a rapid count, as NOAA's and the State's bleaching responses
   * are, the episode's toll wanted while it shows (JUDGED: a week's field days, the shortest survey the boat runs).
   */
  assessmentDays: 5,
  /**
   * A second boat (M277, the redesign's section 4: "additional boat capacity, unlocked later"): DAR's own for its Maui
   * coral reef program, a 27 ft aluminum dive boat with twin 200 HP outboards, "Inventech Marine Services, LLC.
   * submitted the sole bid for $298,976.50" (DLNR BLNR submittal F-3, 27 May 2022, p. 1, a State record, its cost
   * recorded); its upkeep and fuel the *Kilo Kai*'s (JUDGED: the same class of boat). Its days from order to the water
   * JUDGED (four months: no lead time could be read); bought from the State's fiscal year's grant, so only once the
   * first has come (JUDGED, a game's: a capital purchase from a year's budget).
   */
  secondBoat: { name: 'Mālolo', price: 298_977, deliveryDays: 120 },
  /**
   * Volunteer monitoring of an island's reef (M277, the redesign's section 5: "community-supported monitoring"): its
   * volunteers' "8 to 12 transect-based abundance surveys per month" (Polanui Hiu, Lahaina, TNC's Maui Nui CBSFA rules
   * support summary under NOAA CRCP award NA13NOS4820145, p. 3), ten a month here; its cost REEF's fish surveys on
   * Maui, "The cost per year is around $10,000 for one monthly survey and one monthly workshop, including workshop
   * instructors, coordination and supplies" (DLNR, Community Guidebook, 2006, p. 133; nominal dollars, not brought
   * forward: JUDGED); the liaison's month to set it up with the island's community (JUDGED: a first workshop); each
   * volunteers' transect counted as one of NOAA's divers' sites, its error NOAA's scaled by the sites as a survey's
   * (JUDGED: trained to the same count, review M277's; their training to NOAA's error not measured). What it counts is
   * the island's grazing fish; no measured effect of community monitoring on compliance or catch could be found
   * (M277's reader), so it buys knowledge alone.
   */
  community: { costPerYear: 10_000, setupDays: 30, sitesPerMonth: 10 },
  /** The surveys the boat's plan may hold waiting beside the one at sea (JUDGED: a season's work ahead, a list read at a glance). */
  planLength: 4,
  /** The months of accounts kept (two years). */
  monthsKept: 24,
} as const;
